// ======================================================================
// File: PurchaseInvoiceAddGRNDrawer.jsx
// Module: PPE → Procurement → Purchase Invoice
//
// Purpose:
// - Load Available GRNs for Vendor
// - Select GRNs
// - Load Invoiceable GRN Lines
// - Select GRN Lines
// - Return Selected Lines To Parent
// ======================================================================

import React, {
  useEffect,
  useState
} from "react";

import purchaseInvoiceService
  from "../../services/purchaseInvoiceService";

const PurchaseInvoiceAddGRNDrawer = ({
  open,
  onClose,
  vendorId,
  onLinesAdded
}) => {

  const [loading, setLoading] =
    useState(false);

  const [grns, setGrns] =
    useState([]);

  const [selectedGRNs, setSelectedGRNs] =
    useState([]);

  const [grnLines, setGrnLines] =
    useState([]);

  const [selectedLines, setSelectedLines] =
    useState([]);

  // ==========================================================
  // Load Available GRNs
  // ==========================================================

  useEffect(() => {

    if (!open) return;

    if (!vendorId) return;

    loadAvailableGRNs();

  }, [open, vendorId]);

  const loadAvailableGRNs = async () => {

    try {

      setLoading(true);

      const response =
        await purchaseInvoiceService.getAvailableGRNs(
          vendorId
        );

      console.log(
        "AVAILABLE GRNS",
        response
      );

      setGrns(
        response?.data || []
      );

    } catch (error) {

      console.error(
        "Failed to load GRNs",
        error
      );

    } finally {

      setLoading(false);
    }
  };

  // ==========================================================
  // GRN Selection
  // ==========================================================

  const toggleGRN = (
    grnHeaderId
  ) => {

    setSelectedGRNs((prev) => {

      if (
        prev.includes(grnHeaderId)
      ) {
        return prev.filter(
          (id) =>
            id !== grnHeaderId
        );
      }

      return [
        ...prev,
        grnHeaderId
      ];
    });
  };

  // ==========================================================
  // Load GRN Lines
  // ==========================================================

  const loadGRNLines =
    async () => {

      if (
        selectedGRNs.length === 0
      ) {
        alert(
          "Please select at least one GRN"
        );
        return;
      }

      try {

        setLoading(true);

        const response =
          await purchaseInvoiceService.getGRNLines(
            selectedGRNs
          );

        console.log(
          "GRN LINES",
          response
        );

        setGrnLines(
          response?.data || []
        );

      } catch (error) {

        console.error(
          "Failed to load GRN lines",
          error
        );

      } finally {

        setLoading(false);
      }
    };

  // ==========================================================
  // Line Selection
  // ==========================================================

  const toggleLine = (
    lineId
  ) => {

    setSelectedLines((prev) => {

      if (
        prev.includes(lineId)
      ) {
        return prev.filter(
          (id) =>
            id !== lineId
        );
      }

      return [
        ...prev,
        lineId
      ];
    });
  };

  // ==========================================================
  // Add Selected Lines
  // ==========================================================

  const handleAddLines =
    () => {

      const selected =
        grnLines.filter(
          (line) =>
            selectedLines.includes(
              line.id
            )
        );

      const invoiceLines =
        selected.map(
            (row) => ({

            grn_header_id:
                row.grn_header_id,

            grn_line_id:
                row.id,

            grn_no:
                row.grn_no,

            material_type:
                row.material_type,

            material_id:
                row.material_id,

            material_code:
                row.material_code,

            material_name:
                row.material_name,

            accepted_qty:
                row.accepted_qty,

            previously_invoiced_qty:
                row.previously_invoiced_qty,

            available_qty:
                row.available_qty,

            invoice_qty:
                row.available_qty,

            uom:
                row.uom,

            unit_price:
                row.unit_price || 0,

            tax_percent: row.tax_percent || 0,
            cgst_rate: row.cgst_rate || 0,
            sgst_rate: row.sgst_rate || 0,
            igst_rate: row.igst_rate || 0,


            remarks: ""
            })
        );
        //console.log("Invoice lines added:", invoiceLines);
      if (
        onLinesAdded
      ) {
        onLinesAdded(
          invoiceLines
        );
      }

      onClose();
    };

  if (!open) return null;

  return (
    <>
      <div
        className="pi-drawer-backdrop"
        onClick={onClose}
      />

      <div className="pi-drawer">

        {/* ================================================= */}
        {/* HEADER                                            */}
        {/* ================================================= */}

        <div className="pi-drawer-header">

          <h2>
            Add GRN Lines
          </h2>

          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>

        </div>

        {/* ================================================= */}
        {/* BODY                                              */}
        {/* ================================================= */}

        <div className="pi-drawer-body">

          {loading && (
            <div
              style={{
                padding: "10px"
              }}
            >
              Loading...
            </div>
          )}

          {/* ============================================= */}
          {/* AVAILABLE GRNS                                */}
          {/* ============================================= */}

          <h3>
            Available GRNs
          </h3>

          <table
            className="table table-bordered"
          >
            <thead>
              <tr>
                <th />
                <th>GRN No</th>
                <th>Date</th>
                <th>Vendor</th>
              </tr>
            </thead>

            <tbody>

              {grns.map(
                (grn) => (
                  <tr
                    key={
                      grn.grn_header_id
                    }
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={
                          selectedGRNs.includes(
                            grn.grn_header_id
                          )
                        }
                        onChange={() =>
                          toggleGRN(
                            grn.grn_header_id
                          )
                        }
                      />
                    </td>

                    <td>
                      {grn.grn_no}
                    </td>

                    <td>
                      {grn.grn_date}
                    </td>

                    <td>
                      {grn.vendor_name}
                    </td>

                  </tr>
                )
              )}

            </tbody>
          </table>

          <button
            className="btn btn-primary"
            onClick={
              loadGRNLines
            }
          >
            Load GRN Lines
          </button>

          {/* ============================================= */}
          {/* GRN LINES                                      */}
          {/* ============================================= */}

          {grnLines.length > 0 && (
            <>
              <h3
                style={{
                  marginTop:
                    "20px"
                }}
              >
                Invoiceable GRN Lines
              </h3>

              <table
                className="table table-bordered"
              >
                <thead>
                  <tr>
                    <th />
                    <th>GRN</th>
                    <th>Material</th>
                    <th>Code</th>
                    <th>Accepted</th>
                    <th>Previously Inv</th>
                    <th>Available</th>
                  </tr>
                </thead>

                <tbody>

                  {grnLines.map(
                    (line) => (
                      <tr
                        key={line.id}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={
                              selectedLines.includes(
                                line.id
                              )
                            }
                            onChange={() =>
                              toggleLine(
                                line.id
                              )
                            }
                          />
                        </td>

                        <td>
                          {
                            line.grn_no
                          }
                        </td>

                        <td>
                          {
                            line.material_name
                          }
                        </td>

                        <td>
                          {
                            line.material_code
                          }
                        </td>

                        <td>
                          {
                            line.accepted_qty
                          }
                        </td>

                        <td>
                          {
                            line.previously_invoiced_qty
                          }
                        </td>

                        <td>
                          {
                            line.available_qty
                          }
                        </td>

                      </tr>
                    )
                  )}

                </tbody>
              </table>

              <button
                className="btn btn-success"
                onClick={
                  handleAddLines
                }
              >
                Add Selected Lines (
                {
                  selectedLines.length
                }
                )
              </button>
            </>
          )}

        </div>

      </div>
    </>
  );
};

export default PurchaseInvoiceAddGRNDrawer;